import AddIcon from '@mui/icons-material/Add'
import LocalFireDepartmentOutlined from '@mui/icons-material/LocalFireDepartmentOutlined'
import { palettePrimaryMain } from '@/theme/designTokens'

import { Box, Button, Typography } from '@mui/material'
import { useEffect, useMemo, type ReactNode } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import { useMe } from '@/features/auth/api/useAuth'
import {
  clearPostInviteWelcomePending,
  isPostInviteWelcomePending,
  POST_INVITE_WELCOME_QUERY_PARAM,
} from '@/features/dashboard/lib/post-invite-welcome'
import { PageHeader } from '@/shared/components/PageHeader'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useSessionStore } from '@/stores/useSessionStore'

import { PlatformAdminDashboardPage } from '@/features/platform-dashboard/routes/PlatformAdminDashboardPage'
import { AdminDashboard } from '../components/AdminDashboard'
import { EditorViewerDashboard } from '../components/EditorViewerDashboard'
import { SensemakerFtuxTour } from '../components/SensemakerFtuxTour'
import { adminFtuxSteps, editorViewerFtuxSteps } from '../lib/ftux-steps'
import { EDITOR_VIEWER_DASHBOARD_LAYOUT } from '../lib/editor-viewer-dashboard-layout'

function welcomeShortName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'Sensemaker'
}

const dashboardHeroH1Sx = {
  fontWeight: 700,
  fontSize: 24,
  lineHeight: '32px',
  letterSpacing: '0.07px',
  color: '#0f172a',
} as const

const dashboardHeroSubtitleSx = {
  mt: 0.5,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '-0.15px',
  color: '#64748b',
} as const

function DashboardHeroTitleBlock({ subtitle }: Readonly<{ subtitle: ReactNode }>) {
  return (
    <>
      <Typography component="h1" sx={dashboardHeroH1Sx}>
        Dashboard
      </Typography>
      <Typography sx={dashboardHeroSubtitleSx}>{subtitle}</Typography>
    </>
  )
}

function DashboardPageHeader({
  showAdmin,
  showEv,
  showEvBuildCta,
  isPlatformAdmin,
  userName,
}: Readonly<{
  showAdmin: boolean
  showEv: boolean
  showEvBuildCta: boolean
  isPlatformAdmin: boolean
  userName: string | undefined
}>) {
  if (isPlatformAdmin) {
    return null
  }

  if (showAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: '30px',
        }}
      >
        <Box sx={{ minWidth: 240 }}>
          <DashboardHeroTitleBlock subtitle="Overview of your platform metrics and campaigns" />
        </Box>
        <Button
          component={RouterLink}
          to="/campaigns/get-started"
          variant="contained"
          disableElevation
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          data-testid="sensemaker-ftux-create-campaign"
          sx={{
            bgcolor: palettePrimaryMain,
            textTransform: 'none',
            borderRadius: '8px',
            px: 2,
            py: 1.25,
            fontWeight: 500,
            fontSize: 14,
            letterSpacing: '-0.15px',
            '&:hover': { bgcolor: '#007ab8' },
          }}
        >
          Build New Research
        </Button>
      </Box>
    )
  }

  if (showEv) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2.5,
            /* Figma `4154:4576`: strip `h-[99px]` + `items-center` → (99−75)/2 ≈ 12px inset top/bottom */
            py: '12px',
            px: '17px',
            mb: EDITOR_VIEWER_DASHBOARD_LAYOUT.welcomeBannerMarginBottom,
            borderRadius: '10px',
            border: '1px solid #3b82f6',
            bgcolor: '#edf1f8',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: 75,
              height: 75,
              borderRadius: '10px',
              bgcolor: '#0c9eeb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <LocalFireDepartmentOutlined
              sx={{ color: '#fff', fontSize: 42 }}
              aria-hidden
            />
          </Box>
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: 21,
                lineHeight: '27px',
                letterSpacing: '-0.44px',
                color: '#04183f',
              }}
            >
              Welcome to Brushfire, {welcomeShortName(userName)}!
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 14,
                lineHeight: '20px',
                letterSpacing: '-0.15px',
                color: '#616a70',
              }}
            >
              Get started by exploring your allocated instruments below
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ minWidth: 240 }}>
            <DashboardHeroTitleBlock subtitle="View and access your allocated instruments" />
          </Box>
          {/* BFP-999 TC-DIB01 — Editors also enter Get Started from Dashboard */}
          {showEvBuildCta && (
            <Button
              component={RouterLink}
              to="/campaigns/get-started"
              variant="contained"
              disableElevation
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              data-testid="sensemaker-ftux-create-campaign"
              sx={{
                bgcolor: palettePrimaryMain,
                textTransform: 'none',
                borderRadius: '8px',
                px: 2,
                py: 1.25,
                fontWeight: 500,
                fontSize: 14,
                letterSpacing: '-0.15px',
                '&:hover': { bgcolor: '#007ab8' },
              }}
            >
              Build New Research
            </Button>
          )}
        </Box>
      </>
    )
  }

  return <PageHeader title={`Welcome, ${userName ?? 'Sensemaker'}`} />
}

export function DashboardPage() {
  useDocumentTitle('Dashboard')
  const user = useSessionStore((s) => s.user)
  const { data: me, isSuccess } = useMe()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (!isPostInviteWelcomePending(searchParams)) return
    clearPostInviteWelcomePending()
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(POST_INVITE_WELCOME_QUERY_PARAM)
        return next
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  const role = user?.role ?? null
  const ftuxStatus = me?.sensemaker_ftux_status

  const showAdmin = role === 'ADMIN'
  const showEv = role === 'EDITOR' || role === 'VIEWER'
  const showEvBuildCta = role === 'EDITOR'
  const isPlatformAdmin = role === 'PLATFORM_ADMIN'

  const steps = useMemo(() => {
    if (showAdmin) return adminFtuxSteps()
    if (showEv) return editorViewerFtuxSteps()
    return []
  }, [showAdmin, showEv])

  const shouldTour =
    isSuccess && ftuxStatus === 'PENDING' && (showAdmin || showEv)

  return (
    <>
      <DashboardPageHeader
        showAdmin={showAdmin}
        showEv={showEv}
        showEvBuildCta={showEvBuildCta}
        isPlatformAdmin={isPlatformAdmin}
        userName={user?.name}
      />

      {showAdmin && <AdminDashboard />}
      {showEv && <EditorViewerDashboard role={role === 'EDITOR' ? 'EDITOR' : 'VIEWER'} />}
      {isPlatformAdmin && <PlatformAdminDashboardPage />}

      {!showAdmin && !showEv && !isPlatformAdmin && (
        <Typography variant="body2" color="text.secondary">
          Dashboard content for your role is not available.
        </Typography>
      )}

      <SensemakerFtuxTour steps={steps} shouldRun={shouldTour} />
    </>
  )
}
